import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Backdrop from "components/Backdrop";
import ErrorBox from "components/ErrorBox";
import Card from "components/Card";
import ClientUsersTable from "features/client_management/ClientUsersTable";
import { getClientManagementStats } from "api/apiHelper";
import ClientList from "./ClientList";

const ClientManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [totalClients, setTotalClients] = useState<number>(0);
  const [assignedEmployees, setAssignedEmployees] = useState<number>(0);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const stats = await getClientManagementStats();
        setTotalClients(stats.totalClients);
        setAssignedEmployees(stats.assignedEmployees);
      } catch (err: any) {
        setError(err?.response?.data?.error || err.message || "Failed to load client management overview.");
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading && !totalClients && !assignedEmployees) {
    return <Backdrop show={true} />;
  }

  return (
    <div className="mt-5 space-y-6">
      {error && <ErrorBox errorMessage={error} />}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card extra="p-6 bg-white dark:bg-navy-800 rounded-xl shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Clients</p>
              <p className="mt-2 text-3xl font-bold text-navy-700 dark:text-white">{totalClients}</p>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                All registered client accounts
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <i className="fas fa-building text-xl text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>

        <Card extra="p-6 bg-white dark:bg-navy-800 rounded-xl shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Employees Assigned</p>
              <p className="mt-2 text-3xl font-bold text-navy-700 dark:text-white">{assignedEmployees}</p>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Total employee-client assignments
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
              <i className="fas fa-users text-xl text-indigo-600 dark:text-indigo-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Search + Table */}
      <div>
        <div className="mb-4 flex gap-2">
          <input
            type="text"
            placeholder="Search client users by name, email, or client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && setSearchTerm(e.currentTarget.value)}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-800 dark:text-white"
          />
          <button
            onClick={() => navigate("../client-management/create")}
            className="px-6 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-medium flex items-center gap-2 dark:bg-brand-400 dark:hover:bg-brand-500"
          >
            <i className="fas fa-plus"></i>
            Add Client
          </button>
        </div>

        {/* <ClientUsersTable searchTerm={searchTerm} /> */}
        <ClientList />
      </div>
    </div>
  );
};

export default ClientManagement;

